import type { AyanamsaId } from '@astrally/shared';
import type { EphemerisAdapter } from '../ephemeris/adapter.js';
import {
  MS_PER_MINUTE,
  hinduDayForIndex,
  localDayIndexFor,
  localTimeParts,
  panchangaLimbsAt,
  type HinduDay,
  type PanchangaLimbIndices,
  type PanchangaOptions,
} from './panchanga.js';

/**
 * Muhurta — electional astrology. "When should I do X?"
 *
 * Two layers:
 *  1. computeDayWindows() slices one sunrise-to-sunrise day into the named
 *     windows every Indian almanac prints: rahu kaal, yamaganda, gulika,
 *     abhijit, brahma muhurta and the sixteen choghadiya periods. Pure
 *     geometry — the divisions are fractions of daylight and night length,
 *     so they move with the seasons and with latitude.
 *  2. suggestMuhurta() scans a date range, cuts the inauspicious windows out
 *     of the auspicious ones, scores what is left against the day's five
 *     limbs for a specific purpose, and returns a ranked shortlist where
 *     every entry carries the reasons and the cautions in plain English.
 *
 * Honest framing: this is an interpretive tradition about what a time MEANS,
 * not a forecast of what will happen. MUHURTA_DISCLAIMER says so and should
 * be shown wherever these results are.
 */

export const MUHURTA_DISCLAIMER =
  'Muhurta is a tradition for choosing meaningful timing, not a prediction of outcomes. ' +
  'These windows describe what classical texts favour for this kind of act. ' +
  'Pick a time that actually works for the people involved first.';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type WindowKind =
  | 'rahu-kaal'
  | 'yamaganda'
  | 'gulika'
  | 'abhijit'
  | 'brahma-muhurta'
  | 'choghadiya-day'
  | 'choghadiya-night';

export type WindowQuality = 'auspicious' | 'inauspicious' | 'neutral';

export type ChoghadiyaName = 'Udveg' | 'Char' | 'Labh' | 'Amrit' | 'Kaal' | 'Shubh' | 'Rog';

export interface DayWindow {
  kind: WindowKind;
  label: string;
  startUtc: Date;
  endUtc: Date;
  quality: WindowQuality;
  /** One plain-English line saying what this window is and why it is graded so. */
  note: string;
  choghadiya?: ChoghadiyaName;
  /** Set on abhijit when it was cut short by an inauspicious window. */
  trimmed?: boolean;
}

export interface DayWindows {
  /** Sunrise/sunset/vaara this day was built from. */
  day: HinduDay;
  /** Everything below, sorted by start time. */
  windows: DayWindow[];
  rahuKaal: DayWindow;
  yamaganda: DayWindow;
  gulika: DayWindow;
  /** Null on Wednesday, which by convention has no abhijit muhurta. */
  abhijit: DayWindow | null;
  brahmaMuhurta: DayWindow;
  /** Eight slices of daylight, sunrise → sunset. */
  dayChoghadiya: DayWindow[];
  /** Eight slices of night, sunset → next sunrise. */
  nightChoghadiya: DayWindow[];
}

export type MuhurtaPurpose = 'launch' | 'travel' | 'contract' | 'conversation' | 'purchase';

export interface MuhurtaOptions extends PanchangaOptions {
  ayanamsaId?: AyanamsaId;
  /** How many suggestions to return. Default 8. */
  limit?: number;
  /** Ignore fragments shorter than this. Default 25 minutes. */
  minMinutes?: number;
  /** Keep the shortlist spread across days rather than stacked on one. Default 2. */
  maxPerDay?: number;
  /** Include the pre-dawn brahma muhurta as a candidate. Default true. */
  includeBrahmaMuhurta?: boolean;
}

export interface MuhurtaSuggestion {
  purpose: MuhurtaPurpose;
  startUtc: Date;
  endUtc: Date;
  durationMinutes: number;
  /** 0-100. Relative preference inside this scan, never a probability. */
  score: number;
  grade: 'excellent' | 'good' | 'workable';
  /** Local wall-clock labels for the UI, e.g. "2026-08-12", "11:36", "12:24". */
  localDate: string;
  localStart: string;
  localEnd: string;
  vaara: string;
  choghadiya?: ChoghadiyaName;
  isAbhijit: boolean;
  isBrahmaMuhurta: boolean;
  limbs: PanchangaLimbIndices;
  /** Why this window is being offered. Written for someone with zero background. */
  reasons: string[];
  /** What is imperfect about it. Always shown alongside the reasons. */
  cautions: string[];
}

// ---------------------------------------------------------------------------
// The classical per-weekday tables (index 0 = Sunday)
// ---------------------------------------------------------------------------

/** Which eighth of the daylight (1-8) each period falls in, per weekday. */
export const RAHU_KAAL_EIGHTH = [8, 2, 7, 5, 6, 4, 3];
export const YAMAGANDA_EIGHTH = [5, 4, 3, 2, 1, 7, 6];
export const GULIKA_EIGHTH = [7, 6, 5, 4, 3, 2, 1];

/** Choghadiya cycle. Each weekday enters the cycle at a different point. */
export const CHOGHADIYA_CYCLE: ChoghadiyaName[] = [
  'Udveg',
  'Char',
  'Labh',
  'Amrit',
  'Kaal',
  'Shubh',
  'Rog',
];

/** Cycle position the DAY choghadiya starts at, per weekday. */
const CHOGHADIYA_DAY_START = [0, 3, 6, 2, 5, 1, 4];
/** The night sequence always starts five steps further round the cycle. */
const CHOGHADIYA_NIGHT_OFFSET = 5;

export const CHOGHADIYA_QUALITY: Record<ChoghadiyaName, WindowQuality> = {
  Amrit: 'auspicious',
  Shubh: 'auspicious',
  Labh: 'auspicious',
  Char: 'auspicious',
  Udveg: 'inauspicious',
  Kaal: 'inauspicious',
  Rog: 'inauspicious',
};

const CHOGHADIYA_GLOSS: Record<ChoghadiyaName, string> = {
  Amrit: "'nectar' — the slot the tradition rates highest, read as nourishing whatever starts in it",
  Shubh: "'auspicious' — Jupiter's slot, the one used for formal and ceremonial starts",
  Labh: "'gain' — Mercury's slot, traditionally used for money, deals and negotiation",
  Char: "'moving' — Venus's slot, read as favouring travel, errands and anything mobile",
  Udveg: "'agitation' — the Sun's slot, traditionally sat out",
  Kaal: "'loss' — Saturn's slot, traditionally sat out",
  Rog: "'illness' — Mars's slot, traditionally sat out",
};

// ---------------------------------------------------------------------------
// Interval helpers
// ---------------------------------------------------------------------------

interface Span {
  start: number;
  end: number;
}

function fractionOfDay(sunriseMs: number, lengthMs: number, from: number, to: number): Span {
  return { start: sunriseMs + lengthMs * from, end: sunriseMs + lengthMs * to };
}

function overlaps(a: Span, b: Span): boolean {
  return a.start < b.end && b.start < a.end;
}

/** `base` minus every blocker, returned as the remaining fragments in order. */
function subtractSpans(base: Span, blockers: Span[]): Span[] {
  let fragments: Span[] = [base];
  for (const blocker of blockers) {
    const next: Span[] = [];
    for (const frag of fragments) {
      if (!overlaps(frag, blocker)) {
        next.push(frag);
        continue;
      }
      if (blocker.start > frag.start) next.push({ start: frag.start, end: blocker.start });
      if (blocker.end < frag.end) next.push({ start: blocker.end, end: frag.end });
    }
    fragments = next;
  }
  return fragments.filter((f) => f.end > f.start);
}

function longest(spans: Span[]): Span | null {
  let best: Span | null = null;
  for (const s of spans) {
    if (!best || s.end - s.start > best.end - best.start) best = s;
  }
  return best;
}

function toWindow(
  kind: WindowKind,
  label: string,
  span: Span,
  quality: WindowQuality,
  note: string,
  extra: Partial<DayWindow> = {},
): DayWindow {
  return {
    kind,
    label,
    startUtc: new Date(span.start),
    endUtc: new Date(span.end),
    quality,
    note,
    ...extra,
  };
}

// ---------------------------------------------------------------------------
// The day's windows
// ---------------------------------------------------------------------------

/** The classical, untrimmed abhijit span: the 8th of 15 muhurtas of daylight. */
export function abhijitSpan(sunriseUtc: Date, sunsetUtc: Date): { startUtc: Date; endUtc: Date } {
  const sunriseMs = sunriseUtc.getTime();
  const lengthMs = sunsetUtc.getTime() - sunriseMs;
  const span = fractionOfDay(sunriseMs, lengthMs, 7 / 15, 8 / 15);
  return { startUtc: new Date(span.start), endUtc: new Date(span.end) };
}

/**
 * All named windows of the sunrise-to-sunrise day containing `utc`.
 *
 * Rahu kaal, yamaganda and gulika are each a fixed eighth of the daylight,
 * chosen by weekday. Abhijit is the middle muhurta of the day, straddling
 * local noon — except on Wednesday, which has none. Brahma muhurta is the
 * 96-to-48-minute window before sunrise. Choghadiya splits daylight and night
 * into eight parts each.
 */
export function computeDayWindows(
  utc: Date,
  lat: number,
  lon: number,
  adapter: EphemerisAdapter,
  options: PanchangaOptions = {},
): DayWindows {
  const dayIndex = dayIndexContaining(utc, lat, lon, adapter, options.timezone);
  return dayWindowsForIndex(dayIndex, lat, lon, adapter, options.timezone);
}

/** The local day index whose sunrise opened the Hindu day containing `utc`. */
function dayIndexContaining(
  utc: Date,
  lat: number,
  lon: number,
  adapter: EphemerisAdapter,
  timezone?: string,
): number {
  const idx = localDayIndexFor(utc.getTime(), lon, timezone);
  const day = hinduDayForIndex(idx, lat, lon, adapter, timezone);
  return utc.getTime() < day.sunriseUtc.getTime() ? idx - 1 : idx;
}

function dayWindowsForIndex(
  dayIndex: number,
  lat: number,
  lon: number,
  adapter: EphemerisAdapter,
  timezone?: string,
): DayWindows {
  const day = hinduDayForIndex(dayIndex, lat, lon, adapter, timezone);
  const sunriseMs = day.sunriseUtc.getTime();
  const sunsetMs = day.sunsetUtc.getTime();
  const nextSunriseMs = day.nextSunriseUtc.getTime();
  const dayLen = sunsetMs - sunriseMs;
  const nightLen = nextSunriseMs - sunsetMs;
  const weekday = day.vaara.index;

  const eighth = (n: number): Span =>
    fractionOfDay(sunriseMs, dayLen, (n - 1) / 8, n / 8);

  const rahuKaal = toWindow(
    'rahu-kaal',
    'Rahu Kaal',
    eighth(RAHU_KAAL_EIGHTH[weekday]),
    'inauspicious',
    `Rahu Kaal — the eighth of daylight assigned to Rahu on ${day.vaara.englishName}. ` +
      'The single window most Indian households will refuse to start something in.',
  );
  const yamaganda = toWindow(
    'yamaganda',
    'Yamaganda',
    eighth(YAMAGANDA_EIGHTH[weekday]),
    'inauspicious',
    'Yamaganda — a second inauspicious eighth of the day, avoided for new beginnings.',
  );
  const gulika = toWindow(
    'gulika',
    'Gulika Kaal',
    eighth(GULIKA_EIGHTH[weekday]),
    'inauspicious',
    'Gulika Kaal — the eighth ruled by Saturn\'s shadow-point, avoided for anything meant to last.',
  );

  const blockers: Span[] = [
    { start: rahuKaal.startUtc.getTime(), end: rahuKaal.endUtc.getTime() },
    { start: yamaganda.startUtc.getTime(), end: yamaganda.endUtc.getTime() },
    { start: gulika.startUtc.getTime(), end: gulika.endUtc.getTime() },
  ];

  // Abhijit: 8th of the day's 15 muhurtas. Wednesday has none by convention.
  let abhijit: DayWindow | null = null;
  if (weekday !== 3) {
    const full = fractionOfDay(sunriseMs, dayLen, 7 / 15, 8 / 15);
    const usable = longest(subtractSpans(full, blockers));
    if (usable && usable.end - usable.start > MS_PER_MINUTE) {
      const trimmed = usable.end - usable.start < full.end - full.start - 1000;
      abhijit = toWindow(
        'abhijit',
        'Abhijit Muhurta',
        usable,
        'auspicious',
        trimmed
          ? 'Abhijit — the "unconquered" middle muhurta of the day, shortened here because ' +
              'part of it falls inside an inauspicious window, which the tradition says wins.'
          : 'Abhijit — the "unconquered" muhurta straddling local noon. The general-purpose ' +
              'good window, used when nothing better can be arranged.',
        { trimmed },
      );
    }
  }

  const brahmaMuhurta = toWindow(
    'brahma-muhurta',
    'Brahma Muhurta',
    { start: sunriseMs - 96 * MS_PER_MINUTE, end: sunriseMs - 48 * MS_PER_MINUTE },
    'auspicious',
    'Brahma Muhurta — the 48 minutes ending roughly 48 minutes before sunrise. ' +
      'Traditionally the clearest-headed window of the whole 24 hours.',
  );

  const dayStart = CHOGHADIYA_DAY_START[weekday];
  const dayChoghadiya: DayWindow[] = [];
  for (let i = 0; i < 8; i++) {
    const name = CHOGHADIYA_CYCLE[(dayStart + i) % 7];
    dayChoghadiya.push(
      toWindow(
        'choghadiya-day',
        `${name} (day)`,
        fractionOfDay(sunriseMs, dayLen, i / 8, (i + 1) / 8),
        CHOGHADIYA_QUALITY[name],
        `${name} choghadiya, ${CHOGHADIYA_GLOSS[name]}.`,
        { choghadiya: name },
      ),
    );
  }

  const nightStart = (dayStart + CHOGHADIYA_NIGHT_OFFSET) % 7;
  const nightChoghadiya: DayWindow[] = [];
  for (let i = 0; i < 8; i++) {
    const name = CHOGHADIYA_CYCLE[(nightStart + i) % 7];
    nightChoghadiya.push(
      toWindow(
        'choghadiya-night',
        `${name} (night)`,
        fractionOfDay(sunsetMs, nightLen, i / 8, (i + 1) / 8),
        CHOGHADIYA_QUALITY[name],
        `${name} choghadiya, ${CHOGHADIYA_GLOSS[name]}.`,
        { choghadiya: name },
      ),
    );
  }

  const windows = [
    brahmaMuhurta,
    rahuKaal,
    yamaganda,
    gulika,
    ...(abhijit ? [abhijit] : []),
    ...dayChoghadiya,
    ...nightChoghadiya,
  ].sort((a, b) => a.startUtc.getTime() - b.startUtc.getTime());

  return {
    day,
    windows,
    rahuKaal,
    yamaganda,
    gulika,
    abhijit,
    brahmaMuhurta,
    dayChoghadiya,
    nightChoghadiya,
  };
}

// ---------------------------------------------------------------------------
// Purpose profiles
// ---------------------------------------------------------------------------

interface PurposeProfile {
  /** Human label used in reasons, e.g. "launching something new". */
  label: string;
  choghadiya: Partial<Record<ChoghadiyaName, number>>;
  favouredNakshatras: number[];
  avoidedNakshatras: number[];
  favouredVaara: number[];
  avoidedVaara: number[];
  /** Extra weight on a waxing (shukla) Moon. */
  waxingBonus: number;
  /** How much a window after dark counts against this purpose. */
  nightPenalty: number;
  /** Panchak — the last five nakshatras — is only cautioned for some purposes. */
  panchakMatters: boolean;
  vaaraReason: Partial<Record<number, string>>;
}

const PURPOSE_PROFILES: Record<MuhurtaPurpose, PurposeProfile> = {
  launch: {
    label: 'starting something new',
    choghadiya: { Amrit: 18, Shubh: 16, Labh: 14, Char: 6, Udveg: -14, Kaal: -16, Rog: -16 },
    favouredNakshatras: [0, 3, 4, 6, 7, 11, 12, 13, 16, 20, 21, 22, 25, 26],
    avoidedNakshatras: [1, 5, 8, 9, 17, 18, 24],
    favouredVaara: [1, 3, 4, 5],
    avoidedVaara: [2, 6],
    waxingBonus: 6,
    nightPenalty: 22,
    panchakMatters: false,
    vaaraReason: {
      1: 'Monday belongs to the Moon, read as receptive — good for anything that needs people to warm to it.',
      3: "Wednesday is Mercury's day, the weekday for communication, launches and paperwork.",
      4: "Thursday is Jupiter's day, the weekday tradition treats as the most expansive of the seven.",
      5: "Friday is Venus's day, favoured for anything that has to look and feel good.",
    },
  },
  travel: {
    label: 'setting off somewhere',
    choghadiya: { Char: 18, Amrit: 14, Shubh: 12, Labh: 10, Udveg: -14, Kaal: -16, Rog: -18 },
    favouredNakshatras: [0, 3, 4, 6, 7, 12, 16, 21, 22, 26],
    avoidedNakshatras: [1, 2, 5, 8, 9, 15, 18, 24],
    favouredVaara: [1, 3, 4, 5],
    avoidedVaara: [2, 6],
    waxingBonus: 2,
    nightPenalty: 4,
    panchakMatters: true,
    vaaraReason: {
      1: 'Monday is the classical weekday for setting out — the Moon moves fastest of the seven.',
      3: "Wednesday is Mercury's day, and Mercury is the traveller of the grahas.",
      4: 'Thursday is favoured for journeys with a purpose behind them.',
      5: 'Friday is favoured for journeys taken for pleasure or company.',
    },
  },
  contract: {
    label: 'signing or agreeing something',
    choghadiya: { Labh: 18, Amrit: 16, Shubh: 16, Char: 4, Udveg: -16, Kaal: -18, Rog: -16 },
    favouredNakshatras: [3, 7, 11, 12, 13, 16, 20, 21, 25],
    avoidedNakshatras: [1, 5, 8, 9, 17, 18],
    favouredVaara: [3, 4, 5],
    avoidedVaara: [0, 2, 6],
    waxingBonus: 6,
    nightPenalty: 26,
    panchakMatters: false,
    vaaraReason: {
      3: "Wednesday is Mercury's day — the weekday for contracts, terms and fine print.",
      4: "Thursday is Jupiter's day, associated with fairness, which is why agreements are traditionally dated to it.",
      5: 'Friday is favoured for agreements between people who have to keep liking each other.',
    },
  },
  conversation: {
    label: 'having the conversation',
    choghadiya: { Amrit: 18, Shubh: 16, Char: 12, Labh: 10, Udveg: -12, Kaal: -14, Rog: -14 },
    favouredNakshatras: [4, 6, 7, 12, 13, 14, 16, 21, 26],
    avoidedNakshatras: [5, 8, 17, 18],
    favouredVaara: [1, 3, 4, 5],
    avoidedVaara: [2, 6],
    waxingBonus: 3,
    nightPenalty: 8,
    panchakMatters: false,
    vaaraReason: {
      1: 'Monday is the Moon\'s day — the weekday read as softest for anything emotional.',
      3: "Wednesday is Mercury's day, which is simply the weekday for talking.",
      4: 'Thursday is read as generous, which helps when the other person has to hear something hard.',
      5: 'Friday is read as warm and reconciling.',
    },
  },
  purchase: {
    label: 'buying something that should last',
    choghadiya: { Labh: 18, Amrit: 16, Shubh: 14, Char: 4, Udveg: -14, Kaal: -18, Rog: -16 },
    favouredNakshatras: [0, 3, 7, 11, 12, 13, 16, 20, 21, 22, 26],
    avoidedNakshatras: [1, 5, 8, 9, 17, 18],
    favouredVaara: [1, 3, 4, 5],
    avoidedVaara: [2, 6],
    waxingBonus: 6,
    nightPenalty: 22,
    panchakMatters: true,
    vaaraReason: {
      1: 'Monday is favoured for buying anything for the home.',
      3: "Wednesday is Mercury's day, favoured for anything bought to be used and traded.",
      4: "Thursday is Jupiter's day — the classical weekday for buying gold and anything meant to grow in value.",
      5: "Friday is Venus's day, favoured for buying vehicles, clothes and anything beautiful.",
    },
  },
};

/** Weekday + nakshatra pairs that make Sarvartha Siddhi ("all aims achieved") yoga. */
const SARVARTHA_SIDDHI: number[][] = [
  [12, 18, 11, 20, 25, 0, 7], // Sunday
  [3, 4, 7, 16, 21], // Monday
  [0, 2, 8, 25], // Tuesday
  [3, 16, 12, 2, 4], // Wednesday
  [0, 6, 7, 16, 26], // Thursday
  [0, 6, 16, 21, 26], // Friday
  [3, 14, 21], // Saturday
];

/** Yogas the tradition marks as unfit for beginnings (0-based indices). */
const HARSH_YOGAS = new Set([0, 5, 8, 9, 12, 14, 16, 18, 26]);
const SEVERE_YOGAS = new Set([16, 26]); // Vyatipata, Vaidhriti

/** Rikta ("empty") tithis plus the two the tradition flatly avoids. */
const WEAK_TITHI_NUMBERS = new Set([4, 8, 9, 14]);
const STRONG_TITHI_NUMBERS = new Set([2, 3, 5, 7, 10, 11, 13]);

const TITHI_QUALITY_NOTE: Record<number, string> = {
  2: 'Dwitiya is a "bhadra" tithi — read as steady, ordinary, workable.',
  3: 'Tritiya is a "jaya" tithi — read as favouring anything you have to win.',
  5: 'Panchami is a "purna" or full tithi — read as complete and well-supported.',
  7: 'Saptami is a "bhadra" tithi — read as steady and constructive.',
  10: 'Dashami is a "purna" or full tithi — read as solid and finishing.',
  11: 'Ekadashi is a "nanda" tithi — read as joyful and light.',
  13: 'Trayodashi is a "jaya" tithi — read as favouring effort that has to pay off.',
};

// ---------------------------------------------------------------------------
// Ranking
// ---------------------------------------------------------------------------

interface Candidate {
  span: Span;
  source: DayWindow;
  windows: DayWindows;
}

/**
 * Scan a date range and rank the windows that suit a purpose.
 *
 * The scan takes the auspicious windows of every day in range, cuts rahu
 * kaal / yamaganda / gulika out of them, drops the offcuts that are too short
 * to be useful, then scores each surviving fragment against the five limbs
 * reading at its midpoint. Every result carries the reasons it scored well
 * and the cautions it scored badly on — both in plain English.
 */
export function suggestMuhurta(
  purpose: MuhurtaPurpose,
  fromUtc: Date,
  toUtc: Date,
  lat: number,
  lon: number,
  adapter: EphemerisAdapter,
  options: MuhurtaOptions = {},
): MuhurtaSuggestion[] {
  const {
    timezone,
    ayanamsaId = 'lahiri',
    limit = 8,
    minMinutes = 25,
    maxPerDay = 2,
    includeBrahmaMuhurta = true,
  } = options;

  const fromMs = fromUtc.getTime();
  const toMs = toUtc.getTime();
  if (!(toMs > fromMs)) return [];

  const profile = PURPOSE_PROFILES[purpose];
  const firstDay = dayIndexContaining(fromUtc, lat, lon, adapter, timezone);
  const lastDay = localDayIndexFor(toMs, lon, timezone);
  // Guard a runaway range: 92 days is already far past useful electional advice.
  const dayCount = Math.max(1, Math.min(lastDay - firstDay + 1, 92));

  const candidates: Candidate[] = [];
  for (let d = 0; d < dayCount; d++) {
    const windows = dayWindowsForIndex(firstDay + d, lat, lon, adapter, timezone);
    const blockers: Span[] = [windows.rahuKaal, windows.yamaganda, windows.gulika].map((w) => ({
      start: w.startUtc.getTime(),
      end: w.endUtc.getTime(),
    }));

    const sources: DayWindow[] = [
      ...windows.dayChoghadiya.filter((w) => w.quality === 'auspicious'),
      ...windows.nightChoghadiya.filter((w) => w.quality === 'auspicious'),
    ];
    if (windows.abhijit) sources.push(windows.abhijit);
    if (includeBrahmaMuhurta) sources.push(windows.brahmaMuhurta);

    for (const source of sources) {
      const base: Span = { start: source.startUtc.getTime(), end: source.endUtc.getTime() };
      const clipped: Span = { start: Math.max(base.start, fromMs), end: Math.min(base.end, toMs) };
      if (clipped.end <= clipped.start) continue;
      for (const span of subtractSpans(clipped, blockers)) {
        if (span.end - span.start < minMinutes * MS_PER_MINUTE) continue;
        candidates.push({ span, source, windows });
      }
    }
  }

  const scored = candidates
    .map((c) => scoreCandidate(purpose, profile, c, lon, adapter, ayanamsaId, timezone))
    .sort((a, b) => b.score - a.score || a.startUtc.getTime() - b.startUtc.getTime());

  // Spread the shortlist across days, and never offer two windows that overlap.
  const perDay = new Map<string, number>();
  const taken: Span[] = [];
  const out: MuhurtaSuggestion[] = [];
  for (const s of scored) {
    const used = perDay.get(s.localDate) ?? 0;
    if (used >= maxPerDay) continue;
    const span: Span = { start: s.startUtc.getTime(), end: s.endUtc.getTime() };
    if (taken.some((t) => overlaps(t, span))) continue;
    perDay.set(s.localDate, used + 1);
    taken.push(span);
    out.push(s);
    if (out.length >= limit) break;
  }
  return out;
}

function scoreCandidate(
  purpose: MuhurtaPurpose,
  profile: PurposeProfile,
  candidate: Candidate,
  lon: number,
  adapter: EphemerisAdapter,
  ayanamsaId: AyanamsaId,
  timezone?: string,
): MuhurtaSuggestion {
  const { span, source, windows } = candidate;
  const midpoint = new Date((span.start + span.end) / 2);
  const limbs = panchangaLimbsAt(midpoint, adapter, ayanamsaId);
  const vaara = windows.day.vaara;

  const reasons: string[] = [];
  const cautions: string[] = [];
  let delta = 0;

  // --- Choghadiya / abhijit / brahma muhurta ------------------------------
  if (source.choghadiya) {
    const weight = profile.choghadiya[source.choghadiya] ?? 0;
    delta += weight;
    if (weight > 0) {
      reasons.push(`${source.choghadiya} choghadiya, ${CHOGHADIYA_GLOSS[source.choghadiya]}.`);
    }
  }
  if (source.kind === 'abhijit') {
    delta += 14;
    reasons.push(
      source.trimmed
        ? 'Abhijit muhurta, the "unconquered" window around local noon. It is short today ' +
            'because an inauspicious period clips one end of it.'
        : 'Abhijit muhurta, the "unconquered" window around local noon — the tradition\'s ' +
            'general-purpose good time, usable when nothing else lines up.'
    );
  }
  if (source.kind === 'brahma-muhurta') {
    // A named auspicious window, so it takes only half the "not during the
    // day" penalty — but a pre-dawn slot is still a poor fit for a signing.
    delta += 6 - profile.nightPenalty / 2;
    reasons.push(
      'Brahma muhurta, the roughly 48 minutes before first light. Classically the clearest ' +
        'window of the day for anything that needs a quiet head.',
    );
    cautions.push('It is before dawn — only worth choosing if you will genuinely be awake.');
  }
  if (source.kind === 'choghadiya-night') {
    delta -= profile.nightPenalty;
    cautions.push('This is a night choghadiya, so it falls after sunset.');
  }

  // --- Vaara --------------------------------------------------------------
  if (profile.favouredVaara.includes(vaara.index)) {
    delta += 10;
    const why = profile.vaaraReason[vaara.index];
    reasons.push(why ?? `${vaara.englishName} is a favoured weekday for ${profile.label}.`);
  } else if (profile.avoidedVaara.includes(vaara.index)) {
    delta -= 8;
    cautions.push(
      vaara.index === 2
        ? "Tuesday is Mars's day, read as sharp and combative — traditionally avoided for this."
        : vaara.index === 6
          ? "Saturday is Saturn's day, read as slow and obstructive for new starts."
          : `${vaara.englishName} is not a favoured weekday for ${profile.label}.`,
    );
  }

  // --- Nakshatra ----------------------------------------------------------
  if (profile.favouredNakshatras.includes(limbs.nakshatra)) {
    delta += 14;
    reasons.push(
      `The Moon is in ${limbs.nakshatraName}, one of the 27 nakshatras (star-segments the Moon ` +
        `moves through in a month). It is on the classical shortlist for ${profile.label}.`,
    );
  } else if (profile.avoidedNakshatras.includes(limbs.nakshatra)) {
    delta -= 12;
    cautions.push(
      `The Moon is in ${limbs.nakshatraName}, a nakshatra the texts class as sharp or fierce ` +
        `and advise against for ${profile.label}.`,
    );
  }

  // --- Named weekday + nakshatra combinations -----------------------------
  if (SARVARTHA_SIDDHI[vaara.index]?.includes(limbs.nakshatra)) {
    delta += 12;
    reasons.push(
      `${vaara.englishName} plus ${limbs.nakshatraName} forms Sarvartha Siddhi yoga, literally ` +
        '"all aims accomplished" — one of the few combinations said to override a weak day.',
    );
  }
  if (limbs.nakshatra === 7 && vaara.index === 4) {
    delta += 14;
    reasons.push(
      'Thursday with the Moon in Pushya is Guru Pushya yoga, the single most-used window in ' +
        'India for buying gold or starting a venture.',
    );
  } else if (limbs.nakshatra === 7 && vaara.index === 0) {
    delta += 8;
    reasons.push('Sunday with the Moon in Pushya is Ravi Pushya yoga, favoured for new undertakings.');
  }

  // --- Tithi --------------------------------------------------------------
  const n = ((limbs.tithi - 1) % 15) + 1;
  const isAmavasya = limbs.tithi === 30;
  if (isAmavasya) {
    delta -= 12;
    cautions.push(
      'It falls on Amavasya, the new moon — the darkest tithi (lunar day), traditionally kept ' +
        'for ancestors rather than beginnings.',
    );
  } else if (WEAK_TITHI_NUMBERS.has(n)) {
    delta -= 10;
    cautions.push(
      `${limbs.tithiName} is a ${n === 8 ? 'sensitive' : '"rikta" or empty'} tithi (lunar day) — ` +
        'the tradition reads it as thin support for something new.',
    );
  } else if (STRONG_TITHI_NUMBERS.has(n)) {
    delta += 8;
    reasons.push(TITHI_QUALITY_NOTE[n] ?? `${limbs.tithiName} is a well-regarded tithi (lunar day).`);
  }
  if (limbs.paksha === 'shukla' && !isAmavasya) {
    delta += profile.waxingBonus;
    if (profile.waxingBonus >= 5) {
      reasons.push(
        'The Moon is waxing (shukla paksha, the fortnight from new to full) — the half of the ' +
          'month read as growing rather than winding down.',
      );
    }
  }

  // --- Yoga ---------------------------------------------------------------
  if (SEVERE_YOGAS.has(limbs.yoga)) {
    delta -= 14;
    cautions.push(
      `${limbs.yogaName} yoga is running. Yoga here means one of 27 divisions of Sun + Moon ` +
        'longitude, and this is one of the two the tradition says to sit out entirely.',
    );
  } else if (HARSH_YOGAS.has(limbs.yoga)) {
    delta -= 6;
    cautions.push(`${limbs.yogaName} yoga is running, which the texts count among the harsher ones.`);
  }

  // --- Karana -------------------------------------------------------------
  if (limbs.isVishti) {
    delta -= 14;
    cautions.push(
      'Vishti karana (also called Bhadra) is running. A karana is half a tithi, and this is ' +
        'the one half the tradition says to postpone anything new past.',
    );
  }

  // --- Panchak ------------------------------------------------------------
  if (profile.panchakMatters && limbs.nakshatra >= 22) {
    delta -= 6;
    cautions.push(
      'The Moon is in Panchak, the last five nakshatras of the circle. Southward travel and ' +
        'certain purchases are traditionally postponed past it.',
    );
  }

  // --- Always-true positives ---------------------------------------------
  reasons.push(
    'Clear of Rahu Kaal, Yamaganda and Gulika — the three windows of each day the tradition ' +
      'says to simply sit out.',
  );
  if (windows.day.estimated) {
    cautions.push(
      'The Sun does not rise or set here on this date, so 06:00 and 18:00 local were used to ' +
        'divide the day. Treat these windows as indicative only.',
    );
  }

  // 50 is a neutral window. The 0.55 compression keeps a strongly-stacked day
  // near the high 80s rather than pinning every good option to 100, which would
  // throw away the ranking the user actually came for.
  const clamped = Math.max(0, Math.min(100, Math.round(50 + delta * 0.55)));
  const start = localTimeParts(span.start, lon, timezone);
  const end = localTimeParts(span.end, lon, timezone);

  return {
    purpose,
    startUtc: new Date(span.start),
    endUtc: new Date(span.end),
    durationMinutes: Math.round((span.end - span.start) / MS_PER_MINUTE),
    score: clamped,
    grade: clamped >= 80 ? 'excellent' : clamped >= 65 ? 'good' : 'workable',
    localDate: start.date,
    localStart: start.time,
    localEnd: end.time,
    vaara: vaara.englishName,
    choghadiya: source.choghadiya,
    isAbhijit: source.kind === 'abhijit',
    isBrahmaMuhurta: source.kind === 'brahma-muhurta',
    limbs,
    reasons,
    cautions,
  };
}

/** Exported for callers that want to explain the scan itself. */
export function purposeLabel(purpose: MuhurtaPurpose): string {
  return PURPOSE_PROFILES[purpose].label;
}
